import { useEffect, useState } from "react"
import type { editor } from "monaco-editor"
import {
  escapeForRegex,
  type LayerDriftFixIntent,
} from "@/v2-backoffice-client/layer/drift/resolve/layer.drift.resolve"

/** What actually happened, so the host can say so and offer an undo. */
export type LayerDriftFixResult = {
  token: string
  renamedTo: string | null
  occurrences: number
}

export type UseLayerDriftFixInput = {
  /** Null until the document's editor has mounted. */
  editor: editor.IStandaloneCodeEditor | null | undefined
  /** Null unless the URL is addressing this document. */
  intent: LayerDriftFixIntent | null
  /** Fired once the edit is staged, so the host can drop the URL params. */
  onApplied?(result: LayerDriftFixResult): void
}

/**
 * Stages a drift fix into a document's editor: a rename is applied as an
 * unsaved edit, a review lands the cursor on the first reference.
 *
 * Deliberately an edit and not a save. The operator arrives from a drift
 * card having read a diff, not having seen their own document — so the
 * change shows up as a draft they can read, watch redraw on the preview
 * map, undo with Cmd+Z, and save with the button they always use. Going
 * through `executeEdits` rather than `setValue` is what buys the undo,
 * the same way the AI assistant applies its suggestions.
 *
 * For a review, selecting the token is the whole implementation: Monaco
 * highlights the other occurrences itself, which is the behaviour an
 * operator already gets from double-clicking a word.
 */
export function useLayerDriftFix(input: UseLayerDriftFixInput) {
  const { editor: instance, intent, onApplied } = input

  const [result, setResult] = useState<LayerDriftFixResult | null>(null)

  // Keyed on the intent so a second hand-off for the same document runs
  // again, while re-renders in between don't re-apply the first.
  const intentKey = intent ? `${intent.token}→${intent.renameTo ?? ""}` : null
  const [appliedKey, setAppliedKey] = useState<string | null>(null)

  useEffect(() => {
    if (!instance || !intent || !intentKey || intentKey === appliedKey) return

    const model = instance.getModel()
    if (!model) return

    const matches = model.findMatches(
      `\\b${escapeForRegex(intent.token)}\\b`,
      false,
      true,
      true,
      null,
      false,
    )

    setAppliedKey(intentKey)

    if (matches.length === 0) {
      // The document changed since the drift was computed, or the
      // operator already fixed it. Say so rather than silently doing
      // nothing — "I clicked and nothing happened" is worse than "there
      // is nothing here to change".
      const empty = {
        token: intent.token,
        renamedTo: intent.renameTo,
        occurrences: 0,
      }
      setResult(empty)
      onApplied?.(empty)
      return
    }

    if (intent.renameTo) {
      instance.executeEdits(
        "layer-drift-fix",
        matches.map((match) => ({
          range: match.range,
          text: intent.renameTo as string,
        })),
      )
    }

    const first = matches[0].range
    instance.setSelection(first)
    instance.revealRangeInCenterIfOutsideViewport(first)
    instance.focus()

    const applied = {
      token: intent.token,
      renamedTo: intent.renameTo,
      occurrences: matches.length,
    }
    setResult(applied)
    onApplied?.(applied)
  }, [instance, intent, intentKey, appliedKey, onApplied])

  return { result, dismiss: () => setResult(null) }
}
